import { useEffect, useState } from 'react';
import { apiService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import AgreementAcceptances from '../components/AgreementAcceptances';

const SystemSettingsPage = () => {
  const { user } = useAuth();
  const [queueEnabled, setQueueEnabled] = useState(true);
  const [verificationRequired, setVerificationRequired] = useState(false);
  // Drives the warning below: how many people would be refused the
  // moment the switch is turned on.
  const [unverifiedCount, setUnverifiedCount] = useState(0);
  const [resetEmailEnabled, setResetEmailEnabled] = useState(false);
  const [closed, setClosed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadAll = async () => {
    const [queuing, verification, resetEmail, maintenance] = await Promise.all([
      apiService.getQueuingEnabled(),
      apiService.getEmailVerificationSetting(),
      apiService.getPasswordResetEmailEnabled(),
      apiService.getMaintenanceMode()
    ]);
    setClosed(maintenance);
    setQueueEnabled(queuing);
    setResetEmailEnabled(resetEmail);
    setVerificationRequired(verification.enabled);
    setUnverifiedCount(verification.unverifiedCount);
  };

  useEffect(() => {
    loadAll()
      .catch((err: any) => setError(err.message || 'Failed to load system settings'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleVerification = async (next: boolean) => {
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      setVerificationRequired(await apiService.setEmailVerificationRequired(next));
      setSuccess(
        next
          ? 'Students must now confirm their email address before signing in.'
          : 'Email confirmation is no longer required to sign in.'
      );
    } catch (err: any) {
      setError(err.message || 'Failed to update the email verification setting');
    } finally {
      setSaving(false);
    }
  };

  const grandfatherExisting = async () => {
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const count = await apiService.grandfatherExistingUsers();
      setUnverifiedCount(0);
      setSuccess(`${count} existing account${count === 1 ? '' : 's'} marked as confirmed.`);
    } catch (err: any) {
      setError(err.message || 'Failed to update existing accounts');
    } finally {
      setSaving(false);
    }
  };

  const toggleClosed = async (next: boolean) => {
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      setClosed(await apiService.setMaintenanceMode(next));
      setSuccess(
        next
          ? 'The system is now closed to students. Staff can still sign in and work.'
          : 'The system is open again for everyone.'
      );
    } catch (err: any) {
      setError(err.message || 'Failed to update the system status');
    } finally {
      setSaving(false);
    }
  };

  const toggleResetEmail = async (next: boolean) => {
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      setResetEmailEnabled(await apiService.setPasswordResetEmailEnabled(next));
      setSuccess(
        next
          ? '"Forgot password?" now emails a reset link.'
          : '"Forgot password?" now tells students to ask the scholarship office for a temporary password. No emails are sent.'
      );
    } catch (err: any) {
      setError(err.message || 'Failed to update the password reset setting');
    } finally {
      setSaving(false);
    }
  };

  const toggleQueue = async (next: boolean) => {
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      // Reflect what the server stored, not what was clicked — if the
      // write is rejected the switch snaps back rather than lying.
      setQueueEnabled(await apiService.setQueuingEnabled(next));
      setSuccess(
        next
          ? 'Queuing is on. Document uploads take turns when many students upload at once.'
          : 'Queuing is off. Uploads go straight through with no waiting step.'
      );
    } catch (err: any) {
      setError(err.message || 'Failed to update the queuing setting');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <nav className="navbar">
        <div className="navbar-brand">System Settings</div>
        <div className="navbar-actions">
          <div className="user-menu">
            <span>👤</span>
            <span>{user ? `${user.firstName} ${user.lastName}` : ''}</span>
          </div>
        </div>
      </nav>

      <div className="container">
        <div className="page-header">
          <h1>System Settings</h1>
          <p>Settings that change how the system behaves for everyone.</p>
        </div>

        {error && (
          <div className="alert-error" style={{ padding: '1rem', marginBottom: '1.5rem' }}>
            {error}
          </div>
        )}

        {success && (
          <div
            style={{
              padding: '1rem',
              background: 'rgba(16, 185, 129, 0.1)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderRadius: 'var(--radius-md)',
              marginBottom: '1.5rem',
              color: '#34D399'
            }}
          >
            {success}
          </div>
        )}

        {!loading && (
          <div
            className="card"
            style={{ marginBottom: '1.5rem', border: closed ? '2px solid var(--error-text)' : undefined }}
          >
            <div className="card-header">
              <h3>System Open / Closed</h3>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 20rem', minWidth: 0 }}>
                <p style={{ margin: '0 0 0.4rem', fontWeight: 600 }}>
                  {closed ? 'The system is CLOSED to students' : 'The system is open to everyone'}
                </p>
                <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                  When closed, students and visitors see "The system is temporarily closed" on every page and can't
                  sign in or register. Admins, Super Admins and Viewers keep working as normal. Nothing is deleted;
                  reopen it here at any time.
                </p>
              </div>
              <button
                type="button"
                className={closed ? 'btn btn-primary' : 'btn btn-outline'}
                style={closed ? undefined : { color: 'var(--error-text)', borderColor: 'var(--error-text)' }}
                disabled={saving}
                onClick={() => toggleClosed(!closed)}
              >
                {saving ? 'Saving...' : closed ? 'Reopen the system' : 'Close the system'}
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <p>Loading...</p>
        ) : (
          <div className="card">
            <div className="card-header">
              <h3>Queuing</h3>
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '1rem',
                flexWrap: 'wrap'
              }}
            >
              <div style={{ flex: '1 1 20rem' }}>
                <p style={{ margin: '0 0 0.4rem', fontWeight: 600 }}>
                  Waiting line for document uploads
                </p>
                <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                  When on, uploads take turns: only a few files are held in memory and resized at once, and a
                  student waiting sees "In line" on the upload button. This keeps the site responsive when many
                  students upload together.
                </p>
                <p style={{ margin: '0.6rem 0 0', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                  Sign-in and registration no longer show a ticket, whatever this is set to — the server limits
                  password checks on its own, so students only see "Signing in..." for a moment longer when it's
                  busy. Leave this on around deadlines, when uploads are heaviest.
                </p>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.5rem' }}>
                <span
                  style={{
                    fontWeight: 700,
                    color: queueEnabled ? '#34D399' : 'var(--text-secondary)'
                  }}
                >
                  {queueEnabled ? 'On' : 'Off'}
                </span>
                <button
                  type="button"
                  className={queueEnabled ? 'btn btn-outline' : 'btn btn-primary'}
                  disabled={saving}
                  onClick={() => toggleQueue(!queueEnabled)}
                >
                  {saving ? 'Saving...' : queueEnabled ? 'Turn off queuing' : 'Turn on queuing'}
                </button>
              </div>
            </div>
          </div>
        )}

        {!loading && (
          <div className="card" style={{ marginTop: '1.5rem' }}>
            <div className="card-header">
              <h3>Email Confirmation</h3>
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '1rem',
                flexWrap: 'wrap'
              }}
            >
              <div style={{ flex: '1 1 20rem', minWidth: 0 }}>
                <p style={{ margin: '0 0 0.4rem', fontWeight: 600 }}>
                  Require a confirmed email address to sign in
                </p>
                <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                  When on, a student who has not clicked the link in their confirmation email cannot sign in. They
                  are offered a fresh link on the sign-in page. Admin and Super Admin accounts are never blocked by
                  this, so you cannot lock yourself out.
                </p>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.5rem' }}>
                <span style={{ fontWeight: 700, color: verificationRequired ? '#34D399' : 'var(--text-secondary)' }}>
                  {verificationRequired ? 'On' : 'Off'}
                </span>
                <button
                  type="button"
                  className={verificationRequired ? 'btn btn-outline' : 'btn btn-primary'}
                  disabled={saving}
                  onClick={() => toggleVerification(!verificationRequired)}
                >
                  {saving ? 'Saving...' : verificationRequired ? 'Turn off' : 'Turn on'}
                </button>
              </div>
            </div>

            {/* The whole reason this count is fetched. Turning the switch
                on without seeing it is how an entire cohort gets locked
                out in one click. */}
            {unverifiedCount > 0 && (
              <div
                style={{
                  marginTop: '1rem',
                  padding: '1rem',
                  background: 'var(--warning-bg)',
                  border: '1px solid var(--warning-border)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--warning-text)'
                }}
              >
                <p style={{ margin: '0 0 0.5rem', fontWeight: 600 }}>
                  {unverifiedCount.toLocaleString()} existing account
                  {unverifiedCount === 1 ? '' : 's'} {unverifiedCount === 1 ? 'has' : 'have'} never been confirmed
                </p>
                <p style={{ margin: '0 0 0.75rem', fontSize: '0.85rem' }}>
                  Most of them registered while this system could not send email at all, so no confirmation link was
                  ever delivered to them. Switching the requirement on without dealing with these first would lock
                  every one of them out of their own account.
                </p>
                <button className="btn btn-outline btn-sm" type="button" disabled={saving} onClick={grandfatherExisting}>
                  {saving ? 'Working...' : `Mark all ${unverifiedCount.toLocaleString()} as confirmed`}
                </button>
              </div>
            )}
          </div>
        )}

        {!loading && (
          <div className="card" style={{ marginTop: '1.5rem' }}>
            <div className="card-header">
              <h3>Password Reset by Email</h3>
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '1rem',
                flexWrap: 'wrap'
              }}
            >
              <div style={{ flex: '1 1 20rem', minWidth: 0 }}>
                <p style={{ margin: '0 0 0.4rem', fontWeight: 600 }}>
                  Let "Forgot password?" email a reset link
                </p>
                <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                  When off, the "Forgot password?" page sends no email. It tells students to ask the scholarship
                  office for a temporary password — an Admin gives one with <strong>Reset Password</strong> in Users,
                  and the student must choose their own password the first time they sign in with it.
                </p>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.5rem' }}>
                <span style={{ fontWeight: 700, color: resetEmailEnabled ? '#34D399' : 'var(--text-secondary)' }}>
                  {resetEmailEnabled ? 'On' : 'Off'}
                </span>
                <button
                  type="button"
                  className={resetEmailEnabled ? 'btn btn-outline' : 'btn btn-primary'}
                  disabled={saving}
                  onClick={() => toggleResetEmail(!resetEmailEnabled)}
                >
                  {saving ? 'Saving...' : resetEmailEnabled ? 'Turn off' : 'Turn on'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* A record rather than a setting — nothing here is toggled. It
            sits on this page because this is where the agreement's own
            switches live, and because a consent log nobody can find
            proves nothing. Loads on its own, so a failure to fetch it
            leaves the settings above working. */}
        <AgreementAcceptances />
      </div>
    </div>
  );
};

export default SystemSettingsPage;
